import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const HeroSection = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const particles: { x: number; y: number; vx: number; vy: number; r: number; opacity: number }[] = [];
    const count = 80;

    for (let i = 0; i < count; i++) {
      particles.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx: (Math.random() - 0.5) * 0.4,
        vy: (Math.random() - 0.5) * 0.4,
        r: Math.random() * 2 + 1,
        opacity: Math.random() * 0.5 + 0.2,
      });
    }

    let animId: number;
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      particles.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0 || p.x > canvas.width) p.vx *= -1;
        if (p.y < 0 || p.y > canvas.height) p.vy *= -1;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(14, 165, 233, ${p.opacity})`;
        ctx.fill();

        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[j].x - p.x;
          const dy = particles[j].y - p.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(14, 165, 233, ${0.08 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      });
      animId = requestAnimationFrame(draw);
    };
    draw();

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    window.addEventListener('resize', handleResize);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  return (
    <section className="relative min-h-screen bg-ea-black flex items-center overflow-hidden">
      {/* Background image */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/25a64ba261d31eb511b630815a5639f9.jpg"
          alt=""
          className="w-full h-full object-cover object-top opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ea-black via-ea-black/90 to-ea-navy/60"></div>
      </div>

      {/* Particle Canvas */}
      <canvas ref={canvasRef} className="absolute inset-0 pointer-events-none" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-ea-blue/10 border border-ea-blue/30">
              <span className="w-2 h-2 rounded-full bg-ea-blue animate-pulse-slow"></span>
              <span className="text-ea-blue text-sm font-medium">AI-Powered Business Automation</span>
            </div>

            <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight">
              Turn Missed Enquiries<br />
              Into{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-ea-blue to-ea-blue-electric">
                Confirmed Bookings
              </span>
              <br />
              Automatically
            </h1>

            <p className="text-ea-gray text-lg lg:text-xl leading-relaxed max-w-xl">
              AI automation that handles customer enquiries, books appointments, and follows up 24/7 so you never miss another opportunity.
            </p>

            {/* Service tags */}
            <div className="flex flex-wrap items-center gap-2">
              {[
                { icon: 'ri-robot-2-line', label: 'AI Automation' },
                { icon: 'ri-message-3-line', label: 'Chatbots' },
                { icon: 'ri-mail-send-line', label: 'Email Sequences' },
                { icon: 'ri-global-line', label: 'Website Design' },
              ].map((tag) => (
                <span
                  key={tag.label}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-ea-dark-card border border-ea-dark-border text-ea-gray text-xs font-medium"
                >
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className={`${tag.icon} text-ea-blue text-xs`}></i>
                  </div>
                  {tag.label}
                </span>
              ))}
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <Link
                to="/contact"
                className="px-7 py-3.5 bg-ea-blue hover:bg-ea-blue-light text-white font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer flex items-center gap-2 text-base"
                style={{ boxShadow: '0 0 20px rgba(14, 165, 233, 0.3)' }}
              >
                Book a Demo <i className="ri-arrow-right-line"></i>
              </Link>
              <a
                href="#how-it-works"
                className="px-7 py-3.5 border border-ea-blue/50 hover:border-ea-blue text-ea-blue hover:text-ea-blue-light font-semibold rounded-lg transition-all duration-300 whitespace-nowrap cursor-pointer text-base"
              >
                See How It Works
              </a>
            </div>

            {/* Trust Stats */}
            <div className="flex flex-wrap items-center gap-6 pt-4">
              {[
                { value: '5,200+', label: 'Bookings Automated' },
                { value: '24/7', label: 'AI Response Time' },
                { value: '99%', label: 'Client Satisfaction' },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="text-2xl font-bold text-white">{stat.value}</p>
                  <p className="text-ea-gray text-xs">{stat.label}</p>
                </div>
              ))}
            </div>
            <p className="text-ea-gray/40 text-[11px] -mt-3">*Based on client feedback and system performance</p>

            {/* Plans strip */}
            <div className="pt-2">
              <p className="text-ea-gray/50 text-xs uppercase tracking-wider font-medium mb-2.5">Plans from</p>
              <div className="flex flex-wrap gap-2">
                {[
                  { name: 'Starter', price: '£149', period: '/mo' },
                  { name: 'Pro', price: '£299', period: '/mo', popular: true },
                  { name: 'Scale', price: '£599', period: '/mo' },
                  { name: 'Custom', price: 'From £799', period: '/mo' },
                ].map((plan) => (
                  <Link
                    key={plan.name}
                    to="/pricing"
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                      plan.popular
                        ? 'bg-ea-blue/15 border-ea-blue/40 text-white'
                        : 'bg-ea-dark-card border-ea-dark-border text-ea-gray hover:border-ea-blue/30 hover:text-white'
                    }`}
                  >
                    <span className={plan.popular ? 'text-ea-blue' : 'text-ea-gray/60'}>{plan.name}</span>
                    <span className="text-white font-bold">{plan.price}</span>
                    <span className={plan.popular ? 'text-ea-blue/70' : 'text-ea-gray/40'}>{plan.period}</span>
                    {plan.popular && (
                      <span className="ml-0.5 px-1.5 py-0.5 bg-ea-blue/20 rounded text-ea-blue" style={{ fontSize: '9px' }}>Popular</span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </div>

          {/* Right - Enhanced Chatbot Visual */}
          <div className="hidden lg:flex justify-center items-center">
            <div className="relative w-full max-w-sm">

              {/* Floating channel badges top-left */}
              <div className="absolute -left-10 top-6 flex flex-col gap-2 z-20">
                {[
                  { icon: 'ri-whatsapp-line', color: 'text-green-400', bg: 'bg-green-400/10 border-green-400/20', label: 'WhatsApp' },
                  { icon: 'ri-instagram-line', color: 'text-pink-400', bg: 'bg-pink-400/10 border-pink-400/20', label: 'Instagram' },
                  { icon: 'ri-facebook-circle-line', color: 'text-sky-400', bg: 'bg-sky-400/10 border-sky-400/20', label: 'Facebook' },
                ].map((ch) => (
                  <div key={ch.label} className={`flex items-center gap-2 px-3 py-1.5 rounded-full border ${ch.bg} backdrop-blur-sm`}>
                    <div className="w-3 h-3 flex items-center justify-center">
                      <i className={`${ch.icon} ${ch.color} text-xs`}></i>
                    </div>
                    <span className="text-white/70 text-xs whitespace-nowrap">{ch.label}</span>
                  </div>
                ))}
              </div>

              {/* Booking confirmed toast - top right */}
              <div
                className="absolute -top-4 -right-6 z-20 bg-ea-dark-card border border-green-500/30 rounded-xl px-4 py-3 flex items-center gap-3 animate-float"
                style={{ boxShadow: '0 0 20px rgba(34, 197, 94, 0.15)', animationDelay: '1s' }}
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-full bg-green-500/20 flex-shrink-0">
                  <i className="ri-calendar-check-line text-green-400 text-sm"></i>
                </div>
                <div>
                  <p className="text-green-400 text-xs font-semibold">Booking Confirmed</p>
                  <p className="text-ea-gray text-xs">Sarah T. - Tomorrow 10am</p>
                </div>
              </div>

              {/* Main chat card */}
              <div
                className="relative rounded-2xl bg-ea-dark border border-ea-dark-border overflow-hidden animate-float"
                style={{ boxShadow: '0 0 60px rgba(14, 165, 233, 0.2)' }}
              >
                {/* Chat Header */}
                <div className="bg-gradient-to-r from-ea-blue-dark to-ea-blue px-4 py-3.5 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                    <i className="ri-robot-2-line text-white text-lg"></i>
                  </div>
                  <div className="flex-1">
                    <p className="text-white font-semibold text-sm">EA Assistant</p>
                    <div className="flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></span>
                      <p className="text-white/80 text-xs">Always online</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1.5 bg-white/10 rounded-lg px-2 py-1">
                    <span className="text-white/80 text-xs font-medium">3</span>
                    <span className="text-white/60 text-xs">new</span>
                  </div>
                </div>

                {/* Chat Messages */}
                <div className="p-4 space-y-3">
                  <div className="flex justify-start">
                    <div className="max-w-[85%] bg-ea-dark-card border border-ea-dark-border rounded-2xl rounded-tl-sm px-3 py-2.5">
                      <p className="text-white text-xs leading-relaxed">Hi! I noticed you&apos;d like to know more about our services. What type of business do you run?</p>
                      <p className="text-ea-gray text-[10px] mt-1">10:42 PM</p>
                    </div>
                  </div>
                  <div className="flex justify-end">
                    <div className="max-w-[75%] bg-ea-blue rounded-2xl rounded-tr-sm px-3 py-2.5">
                      <p className="text-white text-xs">I run a hair salon</p>
                      <p className="text-white/60 text-[10px] mt-1">10:43 PM</p>
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="max-w-[90%] bg-ea-dark-card border border-ea-dark-border rounded-2xl rounded-tl-sm px-3 py-2.5">
                      <p className="text-white text-xs leading-relaxed">Perfect! I can book you in for a free demo. Which time works best this week?</p>
                      <p className="text-ea-gray text-[10px] mt-1">10:43 PM</p>
                    </div>
                  </div>

                  {/* Quick reply chips */}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {['Tuesday 2pm', 'Wednesday 10am', 'Thursday 4pm'].map((slot) => (
                      <span key={slot} className="px-2.5 py-1 rounded-full border border-ea-blue/40 text-ea-blue text-[10px] font-medium cursor-pointer hover:bg-ea-blue/10 transition-colors whitespace-nowrap">
                        {slot}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-2">
                    <div className="flex-1 bg-ea-dark-card border border-ea-dark-border rounded-lg px-3 py-2">
                      <p className="text-ea-gray/50 text-xs">Type a message...</p>
                    </div>
                    <div className="w-8 h-8 rounded-lg bg-ea-blue flex items-center justify-center flex-shrink-0">
                      <i className="ri-send-plane-fill text-white text-sm"></i>
                    </div>
                  </div>
                </div>
              </div>

              {/* Live stats bar below card */}
              <div className="mt-3 grid grid-cols-3 gap-2">
                {[
                  { icon: 'ri-calendar-check-line', value: '12', label: 'Booked today', color: 'text-green-400' },
                  { icon: 'ri-message-2-line', value: '47', label: 'Replied 24h', color: 'text-ea-blue' },
                  { icon: 'ri-user-add-line', value: '8', label: 'Leads captured', color: 'text-yellow-400' },
                ].map((stat) => (
                  <div key={stat.label} className="bg-ea-dark-card border border-ea-dark-border rounded-xl p-2.5 text-center">
                    <div className="w-4 h-4 flex items-center justify-center mx-auto mb-1">
                      <i className={`${stat.icon} ${stat.color} text-xs`}></i>
                    </div>
                    <p className="text-white font-bold text-sm">{stat.value}</p>
                    <p className="text-ea-gray text-[10px]">{stat.label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-ea-black to-transparent pointer-events-none"></div>
    </section>
  );
};

export default HeroSection;
